import { useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { T, RADIUS } from '@/lib/tokens';
import { useSignedUrl } from '@/hooks/useSignedUrl';
import { useIsMobile } from '@/hooks/useMobile';
import { Camera, ImagePlus, X, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { compressImage } from '@/lib/imageCompression';

interface ImageUploadProps {
  value?: string | null;
  onChange: (url: string | null) => void;
  bucket?: string;
  folder?: string;
  className?: string;
}

/**
 *
 * @param root0
 * @param root0.value
 * @param root0.onChange
 * @param root0.bucket
 * @param root0.folder
 * @param root0.className
 */
export function ImageUpload({
  value,
  onChange,
  bucket = 'item-photos',
  folder = 'items',
  className,
}: ImageUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Invalid file type', { description: 'Please select an image file.' });
      return;
    }

    setIsUploading(true);
    try {
      const compressed = await compressImage(file);

      if (compressed.size > 5 * 1024 * 1024) {
        toast.error('File too large', {
          description: 'Image is still over 5MB after compression. Please select a smaller image.',
        });
        setIsUploading(false);
        return;
      }

      const fileExt = compressed.name.split('.').pop()?.toLowerCase() || 'jpg';
      const fileName = `${folder}/${crypto.randomUUID()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(fileName, compressed);

      if (uploadError) throw uploadError;

      // Store the path, not the public URL (bucket is private)
      onChange(fileName);
      toast.success('Image uploaded!');
    } catch (error) {
      toast.error('Upload failed', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemove = () => {
    onChange(null);
  };

  const { url: signedUrl } = useSignedUrl(value, bucket);

  return (
    <div className={cn('space-y-2', className)}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        className="hidden"
      />

      {value && signedUrl ? (
        <div className="relative inline-block">
          <img
            src={signedUrl}
            alt="Item photo"
            className="h-32 w-32 rounded-lg object-cover border"
          />
          <button
            type="button"
            onClick={handleRemove}
            style={{
              position: 'absolute',
              top: -8,
              right: -8,
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: T.danger,
              color: T.white,
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            }}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="flex gap-2">
          {isMobile && (
            <button
              type="button"
              onClick={() => cameraInputRef.current?.click()}
              disabled={isUploading}
              aria-busy={isUploading}
              style={{
                width: 128,
                height: 128,
                borderRadius: RADIUS.md,
                background: T.surfaceSubtle,
                color: T.textSec,
                border: `1px solid ${T.border}`,
                cursor: isUploading ? 'default' : 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                fontFamily: 'inherit',
                opacity: isUploading ? 0.6 : 1,
              }}
            >
              {isUploading ? (
                <Loader2 className="h-6 w-6 animate-spin" />
              ) : (
                <>
                  <Camera className="h-6 w-6" />
                  <span style={{ fontSize: 12 }}>Take Photo</span>
                </>
              )}
            </button>
          )}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            aria-busy={isUploading}
            style={{
              width: 128,
              height: 128,
              borderRadius: RADIUS.md,
              background: T.surfaceSubtle,
              color: T.textSec,
              border: `1px solid ${T.border}`,
              cursor: isUploading ? 'default' : 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              fontFamily: 'inherit',
              opacity: isUploading ? 0.6 : 1,
            }}
          >
            {isUploading ? (
              <Loader2 className="h-6 w-6 animate-spin" />
            ) : (
              <>
                <ImagePlus className="h-6 w-6" />
                <span style={{ fontSize: 12 }}>{isMobile ? 'From Library' : 'Add Photo'}</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
